import { useEffect } from 'react'
import { useEmailQAStore } from '@/stores/email-qa'
import { QAReviewCard } from './qa-review-card'
import { QAFilters } from './qa-filters'
import { QAAnalytics } from './qa-analytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, ClipboardList } from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface QAReviewListProps {
  currentReviewer?: string
}

export function QAReviewList({ currentReviewer = 'Admin' }: QAReviewListProps) {
  const { reviews, isLoading, error, fetchReviews } = useEmailQAStore()

  useEffect(() => {
    // Debug: Direct query to test RLS
    const testDirectQuery = async () => {
      console.log('🧪 Testing direct Supabase query...')
      const { data, error, count } = await supabase
        .from('email_qa_reviews')
        .select('*', { count: 'exact' })

      console.log('🧪 Direct query result:', {
        data,
        error,
        count,
        rowCount: data?.length,
        firstRow: data?.[0]
      })
    }

    testDirectQuery()
    fetchReviews()

    // Subscribe to realtime updates for email_qa_reviews table
    const channel = supabase
      .channel('email-qa-reviews-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_qa_reviews'
        },
        (payload) => {
          console.log('Email QA Review change detected:', payload)
          // Refetch reviews when any change occurs
          fetchReviews()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchReviews])

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <p>Fehler beim Laden der QA Reviews: {error}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <QAAnalytics />

      {/* Filters */}
      <QAFilters />

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            E-Mail QA Reviews ({reviews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Reviews werden geladen...</span>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Noch keine QA Reviews vorhanden.</p>
              <p className="text-sm">
                Reviews werden automatisch aus EmailDrafter Sessions erstellt.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <QAReviewCard
                  key={review.id}
                  review={review}
                  currentReviewer={currentReviewer}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}